import { useEffect, useRef, useState } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { api, streamSse } from '@/lib/api';
import {
  Sparkles,
  Clapperboard,
  Users,
  MapPin,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
  ArrowRight,
  Loader2,
} from 'lucide-react';

type Phase = 'idle' | 'loading' | 'cards' | 'shots' | 'done' | 'error';

interface EpisodeStatus {
  index: number;
  title?: string;
  scenesCount?: number;
  shotsCount?: number;
  done: boolean;
  skipped?: boolean;
  failed?: boolean;
}

type SceneState = 'pending' | 'running' | 'done' | 'failed';

interface SceneStatus {
  state: SceneState;
  shotsCount?: number;
}

type ScenesMap = Record<number, Record<string, SceneStatus>>;

type DirectorEvent =
  | { event: 'phase'; phase: 'cards' | 'shots'; total?: number; scenesTotal?: number }
  | {
      event: 'cards_done';
      charactersCount: number;
      npcsCount: number;
      locationsCount: number;
      cards: {
        characters: Array<{ id: string; name: string }>;
        npcs: Array<{ id: string; name: string }>;
        locations: Array<{ id: string; name: string }>;
      };
    }
  | {
      event: 'episode_start';
      episodeIndex: number;
      of: number;
      title?: string;
      scenesCount?: number;
    }
  | { event: 'scene_start'; episodeIndex: number; sceneId: string }
  | {
      event: 'scene_done';
      episodeIndex: number;
      sceneId: string;
      shotsCount?: number;
      failed?: boolean;
    }
  | {
      event: 'episode_done';
      episodeIndex: number;
      title?: string;
      scenesCount?: number;
      shotsCount?: number;
      skipped?: boolean;
      failed?: boolean;
    }
  | { event: 'done'; directorInitializedAt: string; importedEpisodes?: number; totalEpisodes?: number; totalScenes?: number; characters?: number; npcs?: number; locations?: number }
  | { event: 'busy' }
  | { event: 'already_done'; plan?: unknown; directorInitializedAt?: string }
  | { event: 'cancelled' }
  | { event: 'error'; message: string; episodeIndex?: number };

interface DirectorStatusSnapshot {
  directorInitializedAt: string | null;
  directorInitializingAt: string | null;
  writerCompletedAt: string | null;
  episodeCount: number | null;
  plan: {
    characters?: Array<{ id: string; name: string }>;
    npcs?: Array<{ id: string; name: string }>;
    locations?: Array<{ id: string; name: string }>;
    episodes?: Array<{
      episodeIndex: number;
      title?: string;
      scenes?: Array<{
        sceneId?: string;
        id?: string;
        shots?: unknown[];
      }>;
    }>;
  } | null;
  running: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  episodeId: string;
  onFinish?: () => void;
  onEnterDirector?: () => void;
}

export function DirectorWorkflowDialog({
  open,
  onOpenChange,
  episodeId,
  onFinish,
  onEnterDirector,
}: Props) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [totalEpisodes, setTotalEpisodes] = useState(0);
  const [totalScenes, setTotalScenes] = useState(0);
  const [episodes, setEpisodes] = useState<Record<number, EpisodeStatus>>({});
  const [currentEps, setCurrentEps] = useState<Set<number>>(new Set());
  const [scenes, setScenes] = useState<ScenesMap>({});
  const [counts, setCounts] = useState<{ characters: number; npcs: number; locations: number }>({
    characters: 0,
    npcs: 0,
    locations: 0,
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [errorEp, setErrorEp] = useState<number | null>(null);
  const ctrlRef = useRef<AbortController | null>(null);
  const startedRef = useRef(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const fetchSnapshot = async (): Promise<'done' | 'running' | 'pending' | 'error'> => {
    try {
      const snap = await api
        .get(`director/episodes/${episodeId}/director/status`)
        .json<DirectorStatusSnapshot>();
      if (snap.episodeCount && snap.episodeCount > 0) {
        setTotalEpisodes(snap.episodeCount);
      }
      const plan = snap.plan;
      if (plan) {
        const sceneTotalFromPlan = (plan.episodes ?? []).reduce(
          (acc, ep) => acc + (ep.scenes?.length ?? 0),
          0,
        );
        if (sceneTotalFromPlan > 0) {
          setTotalScenes(sceneTotalFromPlan);
        }
        setCounts({
          characters: plan.characters?.length ?? 0,
          npcs: plan.npcs?.length ?? 0,
          locations: plan.locations?.length ?? 0,
        });
        const seeded: Record<number, EpisodeStatus> = {};
        const seededScenes: ScenesMap = {};
        for (const ep of plan.episodes ?? []) {
          const epScenes = ep.scenes ?? [];
          const shots = epScenes.reduce(
            (acc, sc) => acc + (sc.shots?.length ?? 0),
            0,
          );
          const allScenesDone = epScenes.length > 0;
          seeded[ep.episodeIndex] = {
            index: ep.episodeIndex,
            title: ep.title,
            scenesCount: epScenes.length,
            shotsCount: shots,
            done: allScenesDone,
          };
          const scMap: Record<string, SceneStatus> = {};
          for (const sc of epScenes) {
            const sid = sc.sceneId || sc.id;
            if (sid) {
              scMap[sid] = {
                state: 'done',
                shotsCount: sc.shots?.length ?? 0,
              };
            }
          }
          if (Object.keys(scMap).length > 0) {
            seededScenes[ep.episodeIndex] = scMap;
          }
        }
        setEpisodes(seeded);
        setScenes(seededScenes);
        if (snap.running) {
          // 推断正在生成的集：未完成的集中取前 3 个标为活跃
          const totalEps = snap.episodeCount ?? 0;
          const activeEps = new Set<number>();
          for (let i = 1; i <= totalEps && activeEps.size < 3; i++) {
            const ep = seeded[i];
            if (!ep || !ep.done) activeEps.add(i);
          }
          setCurrentEps(activeEps);
        } else {
          setCurrentEps(new Set());
        }
      }
      if (snap.directorInitializedAt) {
        setPhase('done');
        return 'done';
      }
      // 已有人物卡 → 进入 shots 阶段;否则 cards 阶段
      const hasCards = (plan?.characters?.length ?? 0) > 0;
      setPhase(hasCards ? 'shots' : 'loading');
      return snap.running ? 'running' : 'pending';
    } catch (err) {
      setPhase('error');
      setErrorMsg(err instanceof Error ? err.message : String(err));
      return 'error';
    }
  };

  const startPolling = () => {
    stopPolling();
    const tick = async () => {
      const result = await fetchSnapshot();
      if (result === 'done' || result === 'error') return;
      pollTimerRef.current = setTimeout(tick, 3000);
    };
    pollTimerRef.current = setTimeout(tick, 3000);
  };

  const start = (reconnect = false) => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (!reconnect) {
      setPhase('loading');
      setErrorMsg(null);
      setErrorEp(null);
    }

    const ctrl = streamSse<DirectorEvent>(
      `director/episodes/${episodeId}/director/init`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      },
      (ev) => {
        if (ev.event === 'phase') {
          setPhase(ev.phase);
          if (typeof ev.total === 'number') setTotalEpisodes(ev.total);
          if (typeof ev.scenesTotal === 'number') setTotalScenes(ev.scenesTotal);
        } else if (ev.event === 'cards_done') {
          setCounts({
            characters: ev.charactersCount,
            npcs: ev.npcsCount,
            locations: ev.locationsCount,
          });
        } else if (ev.event === 'episode_start') {
          setTotalEpisodes(ev.of);
          setCurrentEps((s) => {
            const next = new Set(s);
            next.add(ev.episodeIndex);
            return next;
          });
          if (typeof ev.scenesCount === 'number') {
            setEpisodes((m) => ({
              ...m,
              [ev.episodeIndex]: {
                index: ev.episodeIndex,
                title: ev.title ?? m[ev.episodeIndex]?.title,
                scenesCount: ev.scenesCount,
                shotsCount: m[ev.episodeIndex]?.shotsCount ?? 0,
                done: m[ev.episodeIndex]?.done ?? false,
              },
            }));
          }
        } else if (ev.event === 'scene_start') {
          // 重连时可能错过 episode_start,补入活跃集合
          setCurrentEps((s) => {
            if (s.has(ev.episodeIndex)) return s;
            const next = new Set(s);
            next.add(ev.episodeIndex);
            return next;
          });
          setScenes((m) => {
            const epMap = { ...(m[ev.episodeIndex] ?? {}) };
            const prev = epMap[ev.sceneId];
            // 已完成的场景不要被回退
            if (prev && (prev.state === 'done' || prev.state === 'failed')) {
              return m;
            }
            epMap[ev.sceneId] = { state: 'running' };
            return { ...m, [ev.episodeIndex]: epMap };
          });
        } else if (ev.event === 'scene_done') {
          setScenes((m) => {
            const epMap = { ...(m[ev.episodeIndex] ?? {}) };
            epMap[ev.sceneId] = {
              state: ev.failed ? 'failed' : 'done',
              shotsCount: ev.shotsCount ?? 0,
            };
            return { ...m, [ev.episodeIndex]: epMap };
          });
        } else if (ev.event === 'episode_done') {
          setCurrentEps((s) => {
            if (!s.has(ev.episodeIndex)) return s;
            const next = new Set(s);
            next.delete(ev.episodeIndex);
            return next;
          });
          setEpisodes((m) => ({
            ...m,
            [ev.episodeIndex]: {
              index: ev.episodeIndex,
              title: ev.title ?? m[ev.episodeIndex]?.title,
              scenesCount: ev.scenesCount ?? m[ev.episodeIndex]?.scenesCount ?? 0,
              shotsCount: ev.shotsCount ?? m[ev.episodeIndex]?.shotsCount ?? 0,
              done: !ev.failed && !ev.skipped,
              skipped: ev.skipped,
              failed: ev.failed,
            },
          }));
        } else if (ev.event === 'done') {
          if (typeof ev.totalEpisodes === 'number') setTotalEpisodes(ev.totalEpisodes);
          if (typeof ev.importedEpisodes === 'number') {
            const total = ev.totalEpisodes ?? totalEpisodes;
            setEpisodes((m) => {
              const next = { ...m };
              for (let i = 1; i <= total; i++) {
                if (!next[i]?.done) {
                  next[i] = { ...(next[i] ?? { index: i }), index: i, done: true };
                }
              }
              return next;
            });
          }
          if (typeof ev.characters === 'number') {
            setCounts((c) => ({
              ...c,
              characters: ev.characters ?? c.characters,
              npcs: ev.npcs ?? c.npcs,
              locations: ev.locations ?? c.locations,
            }));
          }
          if (typeof ev.totalScenes === 'number') setTotalScenes(ev.totalScenes);
          ctrlRef.current = null;
          stopPolling();
          void fetchSnapshot().finally(() => {
            setPhase('done');
            onFinish?.();
          });
        } else if (ev.event === 'already_done') {
          ctrlRef.current = null;
          stopPolling();
          void fetchSnapshot().finally(() => {
            setPhase('done');
            onFinish?.();
          });
        } else if (ev.event === 'busy') {
          ctrlRef.current?.abort();
          ctrlRef.current = null;
          startedRef.current = false;
          startPolling();
        } else if (ev.event === 'cancelled') {
          ctrlRef.current?.abort();
          ctrlRef.current = null;
          stopPolling();
          onOpenChange(false);
        } else if (ev.event === 'error') {
          setPhase('error');
          setErrorMsg(ev.message);
          if (typeof ev.episodeIndex === 'number') setErrorEp(ev.episodeIndex);
          setCurrentEps(new Set());
          ctrlRef.current = null;
        }
      },
      (err) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setPhase('error');
        setErrorMsg(err instanceof Error ? err.message : String(err));
        setCurrentEps(new Set());
        ctrlRef.current = null;
      },
    );
    ctrlRef.current = ctrl;
  };

  useEffect(() => {
    if (open) {
      fetchSnapshot().then((result) => {
        if (result === 'done') return;
        if (result === 'running') {
          // 后台任务运行中 → 重连 SSE 拿实时事件(scene_start / scene_done)
          if (!startedRef.current) {
            setTimeout(() => start(true), 30);
          }
          return;
        }
        if (result === 'pending' && !startedRef.current) {
          setTimeout(start, 30);
        }
      });
    } else {
      ctrlRef.current?.abort();
      ctrlRef.current = null;
      startedRef.current = false;
      stopPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const retry = () => {
    startedRef.current = false;
    setPhase('idle');
    setErrorMsg(null);
    setErrorEp(null);
    setTimeout(start, 30);
  };

  const handleClose = () => {
    ctrlRef.current?.abort();
    ctrlRef.current = null;
    onOpenChange(false);
  };

  const completedEpisodes = Object.values(episodes).filter((e) => e.done).length;
  const completedScenes = Object.values(scenes).reduce(
    (acc, m) =>
      acc +
      Object.values(m).filter((s) => s.state === 'done' || s.state === 'failed').length,
    0,
  );
  const shotsProgress =
    totalScenes > 0
      ? Math.round((completedScenes / totalScenes) * 100)
      : totalEpisodes > 0
      ? Math.round((completedEpisodes / totalEpisodes) * 100)
      : 0;
  const cardsDone = counts.characters > 0 || counts.locations > 0;
  const cardsProgress = cardsDone ? 100 : phase === 'cards' || phase === 'loading' ? 30 : 0;
  const currentEpsList = Array.from(currentEps).sort((a, b) => a - b);

  const isRunning = phase === 'loading' || phase === 'cards' || phase === 'shots';

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
        else onOpenChange(true);
      }}
      title="制作台初始化"
      description="制作台初始化：生成人物卡 / 场地卡，并导入分集场景数据"
      className="max-w-2xl"
      footer={
        <div className="ml-auto flex gap-2">
          {phase === 'error' && (
            <Button variant="secondary" size="sm" onClick={retry}>
              <RotateCcw className="h-4 w-4" />
              重试(已完成集不重做)
            </Button>
          )}
          {phase === 'done' ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                关闭
              </Button>
              <Button
                variant="agent"
                agentColor="director"
                size="sm"
                onClick={() => {
                  onEnterDirector?.();
                  onOpenChange(false);
                }}
              >
                进入制作工作台 <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button variant="ghost" size="sm" onClick={handleClose} disabled={phase === 'loading'}>
              {isRunning ? '隐藏(后台继续)' : '关闭'}
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-5 py-3 text-sm">
        {/* Stepper */}
        <div className="flex items-center gap-2 text-xs">
          <StepChip
            icon={<Users className="h-3.5 w-3.5" />}
            label={`角色 / 场地卡 ${counts.characters || '·'}人 ${counts.locations || '·'}景`}
            active={(phase === 'cards' || phase === 'loading') && !cardsDone}
            done={cardsDone}
          />
          <span className="text-outline">→</span>
          <StepChip
            icon={<Clapperboard className="h-3.5 w-3.5" />}
            label={
              totalScenes > 0
                ? `场景导入 ${completedScenes}/${totalScenes} 场`
                : `场景导入 ${completedEpisodes}/${totalEpisodes || '?'} 集`
            }
            active={phase === 'shots'}
            done={phase === 'done'}
          />
          <span className="text-outline">→</span>
          <StepChip
            icon={<CheckCircle2 className="h-3.5 w-3.5" />}
            label="完成"
            active={false}
            done={phase === 'done'}
          />
        </div>

        {/* 错误 */}
        {phase === 'error' && errorMsg && (
          <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft/30 p-3 text-danger">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex-1 space-y-1">
              <strong className="text-sm">Agent 中断</strong>
              <p className="text-xs leading-relaxed">{errorMsg}</p>
              {errorEp !== null && (
                <p className="text-[11px] opacity-80">
                  卡在第 {errorEp} 集 · 重试将从断点继续
                </p>
              )}
            </div>
          </div>
        )}

        {/* Phase A · 人物 / 场地卡 */}
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-1.5 text-sm font-semibold">
              <Users className="h-4 w-4 text-brand-600" />
              人物卡 / 场地卡
            </h3>
            <span className="text-xs tabular-nums text-neutral-500">
              {counts.characters > 0
                ? `角色 ${counts.characters} · NPC ${counts.npcs} · 场地 ${counts.locations}`
                : '准备中…'}
            </span>
          </div>
          <div className="relative h-2 w-full overflow-hidden rounded-full bg-surface-variant">
            <div
              className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-600 shadow-[0_0_8px_rgba(245,158,11,0.4)] transition-[width] duration-500 ease-out"
              style={{ width: `${cardsProgress}%` }}
            />
            {cardsProgress > 0 && cardsProgress < 100 && isRunning && (
              <div className="pointer-events-none absolute inset-0 -translate-x-full animate-pulse bg-gradient-to-r from-transparent via-white/30 to-transparent" />
            )}
          </div>
          {(phase === 'loading' || phase === 'cards') && !cardsDone && (
            <div className="flex items-center gap-2 rounded-md bg-amber-50/60 px-2.5 py-1.5 text-xs text-amber-700">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
              </span>
              正在基于 Story Atlas 提取角色 / NPC,并归纳场地光线与道具…
            </div>
          )}
          {counts.characters > 0 && (
            <div className="grid grid-cols-3 gap-1.5">
              <CardChip
                icon={<Users className="h-3 w-3" />}
                label={`主要人物 × ${counts.characters}`}
              />
              <CardChip
                icon={<Sparkles className="h-3 w-3" />}
                label={`NPC × ${counts.npcs}`}
              />
              <CardChip
                icon={<MapPin className="h-3 w-3" />}
                label={`场地 × ${counts.locations}`}
              />
            </div>
          )}
        </section>

        {/* Phase B · 逐集场景数据导入 */}
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-1.5 text-sm font-semibold">
              <Clapperboard className="h-4 w-4 text-brand-600" />
              场景数据导入
            </h3>
            <span className="text-xs tabular-nums text-neutral-500">
              {totalScenes > 0 ? (
                <>
                  {completedScenes}/{totalScenes} 场
                  <span className="ml-1 text-neutral-400">
                    · {completedEpisodes}/{totalEpisodes || '?'} 集
                  </span>
                </>
              ) : totalEpisodes > 0 ? (
                `${completedEpisodes} / ${totalEpisodes} 集`
              ) : (
                '等待人物卡完成…'
              )}
            </span>
          </div>
          <div className="relative h-2 w-full overflow-hidden rounded-full bg-surface-variant">
            <div
              className="h-full rounded-full bg-gradient-to-r from-brand-400 to-brand-600 shadow-[0_0_8px_rgba(99,102,241,0.4)] transition-[width] duration-500 ease-out"
              style={{ width: `${shotsProgress}%` }}
            />
            {shotsProgress > 0 && shotsProgress < 100 && isRunning && (
              <div className="pointer-events-none absolute inset-0 -translate-x-full animate-pulse bg-gradient-to-r from-transparent via-white/30 to-transparent" />
            )}
          </div>
          {phase === 'shots' && currentEpsList.length > 0 && (
            <div className="flex items-center gap-2 rounded-md bg-brand-50/60 px-2.5 py-1.5 text-xs text-brand-700">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-500" />
              </span>
              正在导入 第 {currentEpsList.join(', ')} 集场景数据
            </div>
          )}
          {totalEpisodes > 0 && (
            <div className="grid max-h-44 grid-cols-8 gap-1 overflow-y-auto rounded-lg bg-surface-variant/30 p-2 sm:grid-cols-12">
              {Array.from({ length: totalEpisodes }, (_, i) => i + 1).map((idx) => {
                const ep = episodes[idx];
                const active = currentEps.has(idx);
                const done = ep?.done;
                const failed = ep?.failed;
                const skipped = ep?.skipped;
                const epScenes = scenes[idx] ?? {};
                const epSceneTotal = Object.keys(epScenes).length;
                const epSceneDone = Object.values(epScenes).filter(
                  (s) => s.state === 'done' || s.state === 'failed',
                ).length;
                return (
                  <div
                    key={idx}
                    title={
                      ep?.title
                        ? `第 ${idx} 集 · ${ep.title}${
                            epSceneTotal > 0 ? ` · ${epSceneDone}/${epSceneTotal} 场` : ''
                          } · ${ep.shotsCount ?? 0} 镜`
                        : `第 ${idx} 集`
                    }
                    className={
                      failed
                        ? 'flex h-6 items-center justify-center rounded bg-danger/70 text-[10px] font-medium text-white'
                        : done
                        ? 'flex h-6 items-center justify-center rounded bg-brand-500 text-[10px] font-medium text-white'
                        : skipped
                        ? 'flex h-6 items-center justify-center rounded bg-neutral-300 text-[10px] font-medium text-neutral-600'
                        : active
                        ? 'flex h-6 items-center justify-center rounded bg-brand-200 text-[10px] font-medium text-brand-700 ring-2 ring-brand-500'
                        : 'flex h-6 items-center justify-center rounded bg-surface text-[10px] text-neutral-400'
                    }
                  >
                    {idx}
                  </div>
                );
              })}
            </div>
          )}
          {/* 每集 S 场景级进度(已开始的集都常驻显示) */}
          {(() => {
            const epsWithScenes = new Set<number>();
            Object.keys(scenes).forEach((k) => epsWithScenes.add(Number(k)));
            currentEpsList.forEach((i) => epsWithScenes.add(i));
            Object.values(episodes).forEach((e) => {
              if (e.done || e.failed || e.skipped) epsWithScenes.add(e.index);
            });
            const epList = Array.from(epsWithScenes).sort((a, b) => a - b);
            if (epList.length === 0) return null;
            return (
              <div className="max-h-72 space-y-1.5 overflow-y-auto rounded-lg border border-outline-variant/30 bg-surface-variant/20 p-2">
                {epList.map((idx) => {
                  const ep = episodes[idx];
                  const epScenes = scenes[idx] ?? {};
                  const sceneIds = Object.keys(epScenes).sort(_sceneIdCompare);
                  const total = ep?.scenesCount ?? sceneIds.length;
                  const doneCount = Object.values(epScenes).filter(
                    (s) => s.state === 'done' || s.state === 'failed',
                  ).length;
                  const isActive = currentEps.has(idx);
                  const allDone = Boolean(ep?.done);
                  const failed = Boolean(ep?.failed);
                  const skipped = Boolean(ep?.skipped);
                  return (
                    <div
                      key={idx}
                      className={
                        failed
                          ? 'rounded-md border border-danger/30 bg-danger-soft/20 p-1.5'
                          : isActive
                          ? 'rounded-md border border-brand-300/60 bg-brand-50/50 p-1.5'
                          : allDone
                          ? 'rounded-md border border-success/20 bg-success-soft/15 p-1.5'
                          : skipped
                          ? 'rounded-md border border-outline-variant/30 bg-neutral-100/60 p-1.5'
                          : 'rounded-md border border-outline-variant/30 bg-surface p-1.5'
                      }
                    >
                      <div className="mb-1 flex items-center justify-between gap-2 text-[11px]">
                        <div className="flex min-w-0 items-center gap-1.5">
                          <span
                            className={
                              failed
                                ? 'shrink-0 font-semibold text-danger'
                                : isActive
                                ? 'shrink-0 font-semibold text-brand-700'
                                : allDone
                                ? 'shrink-0 font-semibold text-success'
                                : 'shrink-0 font-semibold text-neutral-700'
                            }
                          >
                            第 {idx} 集
                          </span>
                          {ep?.title && (
                            <span className="truncate text-neutral-500">{ep.title}</span>
                          )}
                          {isActive && (
                            <Loader2 className="h-3 w-3 shrink-0 animate-spin text-brand-500" />
                          )}
                        </div>
                        <span className="shrink-0 tabular-nums text-neutral-500">
                          {doneCount}/{total || '?'} 场
                          {ep?.shotsCount ? (
                            <span className="ml-1 text-neutral-400">· {ep.shotsCount} 镜</span>
                          ) : null}
                        </span>
                      </div>
                      {sceneIds.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {sceneIds.map((sid) => {
                            const sc = epScenes[sid];
                            const cls =
                              sc.state === 'failed'
                                ? 'bg-danger/80 text-white'
                                : sc.state === 'done'
                                ? 'bg-brand-500 text-white'
                                : sc.state === 'running'
                                ? 'bg-brand-100 text-brand-700 ring-1 ring-brand-400'
                                : 'bg-surface text-neutral-400 ring-1 ring-outline-variant/40';
                            return (
                              <span
                                key={sid}
                                title={
                                  sc.state === 'done'
                                    ? `${sid} · 完成 · ${sc.shotsCount ?? 0} 镜`
                                    : sc.state === 'failed'
                                    ? `${sid} · 失败`
                                    : sc.state === 'running'
                                    ? `${sid} · 生成中…`
                                    : `${sid} · 等待`
                                }
                                className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium ${cls}`}
                              >
                                {sc.state === 'running' && (
                                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                                )}
                                {sid}
                                {sc.state === 'done' && sc.shotsCount ? (
                                  <span className="text-[9px] opacity-80">·{sc.shotsCount}</span>
                                ) : null}
                              </span>
                            );
                          })}
                        </div>
                      ) : skipped ? (
                        <div className="text-[10px] text-neutral-400">本集无场景 · 已跳过</div>
                      ) : (
                        <div className="text-[10px] text-neutral-400">准备导入场景…</div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </section>

        {phase === 'done' && (
          <div className="flex items-start gap-2 rounded-lg border border-success/30 bg-success-soft/30 p-3 text-success">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1 text-sm">
                <strong>制作台初始化完成</strong>
                <p className="mt-0.5 text-xs leading-relaxed text-success/80">
                  角色 {counts.characters} · NPC {counts.npcs} · 场地 {counts.locations} · 已导入{' '}
                  {completedEpisodes}/{totalEpisodes} 集 · 共 {completedScenes} 场，可进入制作工作台开始生成分镜
                </p>
              </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** 解析 sceneId 形如 "S12" → 数字 12,失败回退到字典序 */
function _sceneIdCompare(a: string, b: string): number {
  const re = /\d+/;
  const ma = a.match(re);
  const mb = b.match(re);
  if (ma && mb) {
    const da = parseInt(ma[0], 10);
    const db = parseInt(mb[0], 10);
    if (da !== db) return da - db;
  }
  return a.localeCompare(b);
}

function StepChip({
  icon,
  label,
  active,
  done,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  done: boolean;
}) {
  return (
    <span
      className={
        done
          ? 'inline-flex items-center gap-1 rounded-full bg-success-soft/60 px-2.5 py-1 text-success'
          : active
          ? 'inline-flex items-center gap-1 rounded-full bg-brand-100/80 px-2.5 py-1 text-brand-700 ring-1 ring-brand-300'
          : 'inline-flex items-center gap-1 rounded-full bg-surface-variant/50 px-2.5 py-1 text-neutral-500'
      }
    >
      {icon}
      <span className="font-medium">{label}</span>
    </span>
  );
}

function CardChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-50 px-2 py-1 text-xs text-amber-700">
      {icon}
      <span className="font-medium">{label}</span>
    </div>
  );
}
